import './styles.css';
import './App.css';

import { ModalProvider } from './helpers/hooks/modalContext';
import { useEffect, useState } from 'react';
import useVersion from './helpers/hooks/useVersion';
import ModalContainer from './helpers/ModalContainer';

import FetchCompanySettings from './helpers/FetchCompanySettings';

import {
  createLoginToken,
  NUVEI_ENV,
  NUVEI_MERCHANT_ID,
  NUVEI_MERCHANT_SITE_ID,
} from './auth/_requests';
import { useAuth } from './auth/Auth';

import { useUTMTracker } from './helpers/utils/utils';
import AppRouter from './router/AppRouter';
import { SafeCharge } from './helpers/utils/types';
import { PointsProvider } from './helpers/hooks/pointsContext';
import { PaymentOptionsProvider } from './helpers/hooks/PaymentOptionsContext';
import { PointTasksProvider } from './helpers/hooks/pointTasksContext';
import { captureDeepLinkFromLocation } from './helpers/utils/deepLink';
import { usePageAnalytics } from './helpers/hooks/usePageAnalytics';
import { withNuveiLogging } from './helpers/utils/safeSentryError';

declare global {
  interface Window {
    SafeCharge: unknown;
  }
}

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const scriptElem = Object.assign(document.createElement('script'), {
      type: 'text/javascript',
      defer: true,
      src,
      onerror: (e: Event) => {
        reject(e);
      },
    });
    scriptElem.onload = () => {
      resolve();
    };
    document.body.appendChild(scriptElem);
  });

export {
  DEEP_LINK_STORAGE_KEY as LEASE_UPDATE_DEEP_LINK_KEY,
  LEGACY_LEASE_UPDATE_DEEP_LINK_KEY,
} from './helpers/utils/deepLink';

function App() {
  usePageAnalytics();
  const [safeCharge, setSafeCharge] = useState<unknown>(null);
  const { auth, saveAuth } = useAuth();

  useEffect(() => {
    captureDeepLinkFromLocation();
  }, []);

  useEffect(() => {
    loadScript('https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js').then(
      () => {
        // No blockCards config: with it the SDK calls /getCardDetails.do internally on every card add
        setSafeCharge(
          withNuveiLogging(
            (
              window.SafeCharge as {
                (config: {
                  env: string;
                  merchantId: string;
                  merchantSiteId: string;
                }): unknown;
              }
            )({
              env: NUVEI_ENV,
              merchantId: NUVEI_MERCHANT_ID,
              merchantSiteId: NUVEI_MERCHANT_SITE_ID,
            }) as object
          )
        );
      }
    );
  }, []);
  useVersion('1');
  useUTMTracker();

  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!auth?.data.token) return;
        const response = await createLoginToken(auth?.data.token || '');
        const loginToken = response.data.loginToken;
        const newAuth = {
          message: 'success',
          data: {
            tenantId: auth?.data.tenantId || '',
            token: loginToken,
          },
        };
        saveAuth(newAuth);
      } catch (error) {
        console.error(error);
      }
    };

    // JWT token expiry time is 1hr
    const expiryTime = 3600000;
    const refreshInterval = expiryTime - 120000; // Refresh 2 minutes before expiry
    const intervalId = setInterval(fetchToken, refreshInterval);
    fetchToken();

    return () => clearInterval(intervalId);
  }, [auth?.data.token]);

  return (
    <PointsProvider>
      <PointTasksProvider>
        <PaymentOptionsProvider>
          <ModalProvider>
            <ModalContainer safeCharge={safeCharge} />
            <FetchCompanySettings />
            <AppRouter safeCharge={safeCharge as SafeCharge} />
          </ModalProvider>
        </PaymentOptionsProvider>
      </PointTasksProvider>
    </PointsProvider>
  );
}

export default App;
